import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  bringForward,
  bringToFront,
  deleteElement,
  sendBackward,
  sendToBack,
  setSelectedId,
} from "@/store/slideEditorSlice";
import type { SlideElement } from "@ai-teach/types";
import { IconButton } from "@ai-teach/ui-web";
import {
  BiCaretDown,
  BiCaretUp,
  BiChevronsDown,
  BiChevronsUp,
  BiLayerPlus,
  BiMicrophone,
  BiPlayCircle,
  BiSquare,
  BiText,
  BiTrash,
} from "react-icons/bi";
import { BsQuestionSquare } from "react-icons/bs";
import { PiSpeakerHighFill } from "react-icons/pi";
import { LineRenderer } from "../../editor/renderers/LineRenderer";
import { ShapeRenderer } from "../../editor/renderers/ShapeRenderer";

export function LayersPanel() {
  const { slide, selectedId } = useAppSelector((state) => state.slideEditor.present);

  const dispatch = useAppDispatch();

  if (!slide) return null;

  const elements = slide.elements;

  // Sort elements by z-index from higher to lower
  const sortedElements = [...elements].sort((a, b) => b.zIndex - a.zIndex);

  // Get icon based on element type
  const getElementIcon = (element: SlideElement) => {
    switch (element.type) {
      case "shape":
        return <ShapeRenderer element={element} />;
      case "line":
        return (
          <div className="relative w-full h-full">
            <LineRenderer element={{ ...element, startX: 0, startY: 0, endX: 100, endY: 100 }} />
          </div>
        );
      case "image":
        return (
          <img
            alt={element.media.filename ?? "image"}
            src={element.media.src ?? ""}
            className="w-full h-full object-contain"
          />
        );
      case "text":
        return <BiText className="w-5 h-5" />;
      case "video":
        return <BiPlayCircle className="w-5 h-5" />;
      case "audioPlayer":
      case "phonic":
        return <PiSpeakerHighFill className="w-5 h-5" />;
      case "recordButton":
        return <BiMicrophone className="w-5 h-5" />;
      case "dragMatchingText":
      case "dragMatchingImage":
      case "textChoice":
      case "imageChoice":
      case "oddTextOut":
      case "oddImageOut":
      case "fillBlanks":
      case "answerImage":
      case "spelling":
      case "speaking":
      case "composition":
        return <BsQuestionSquare className="w-5 h-5" />;
      default:
        return <BiSquare className="w-5 h-5" />;
    }
  };

  // Get element display name
  const getElementName = (element: SlideElement) => {
    switch (element.type) {
      case "shape":
        return element.shapeType?.charAt(0).toUpperCase() + element.shapeType?.slice(1) || "Shape";
      case "image":
        return element.media.filename
          ? element.media.filename.charAt(0).toUpperCase() +
              element.media.filename.slice(1).toLowerCase()
          : "Image";
      case "text":
        return (
          element.content?.substring(0, 15) + (element.content?.length > 15 ? "..." : "") || "Text"
        );
      case "video":
        return element.media.filename
          ? element.media.filename.charAt(0).toUpperCase() +
              element.media.filename.slice(1).toLowerCase()
          : "Image";
      case "audioPlayer":
        return "Audio Player";
      case "dragMatchingText":
      case "dragMatchingImage":
      case "textChoice":
      case "imageChoice":
      case "oddTextOut":
      case "oddImageOut":
      case "fillBlanks":
      case "answerImage":
      case "spelling":
      case "speaking":
      case "composition":
        return "Question";
      default:
        return "Element";
    }
  };

  const getElementType = (type: string) => {
    const array = type.split("");

    const typeName = array.map((char) => (char.toUpperCase() !== char ? char : ` ${char}`));

    const normalCase = typeName.join("").toLowerCase();

    return normalCase.charAt(0).toUpperCase() + normalCase.slice(1);
  };

  return (
    <div className="px-2 py-4">
      {/* Section header */}
      <div className="mb-4 px-1">
        <h3 className="text-sm font-semibold uppercase tracking-wider">Layers</h3>
        <p className="text-xs text-slate-400 mt-1">Click to select, use buttons to arrange</p>
      </div>

      {/* Elements list */}
      <div className="flex flex-col gap-2">
        {sortedElements.map((element, index) => {
          const isSelected = element.id === selectedId;
          const isTopLayer = index === 0;
          const isBottomLayer = index === sortedElements.length - 1;

          return (
            <div
              key={element.id}
              className={`group relative overflow-hidden rounded-xl border-2 transition-all duration-200 cursor-pointer
                ${
                  isSelected
                    ? "border-primary-400 bg-gradient-to-br from-primary-50 to-accent-50 shadow-lg scale-[1.02]"
                    : "border-slate-200 hover:border-slate-300 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0"
                }`}
              onClick={() => dispatch(setSelectedId(element.id))}
            >
              {/* Subtle gradient overlay for selected state */}
              {isSelected && (
                <div className="absolute inset-0 bg-gradient-to-r from-primary-500/5 to-accent-500/5 pointer-events-none" />
              )}

              {/* Main Content */}
              <div className="relative p-3">
                <div className="flex items-center gap-3 mb-3">
                  {/* Layer Number Badge */}
                  <div
                    className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-200
                      ${
                        isSelected
                          ? "bg-gradient-to-br from-primary-500 to-accent-500 text-white shadow-md"
                          : "bg-slate-100 text-slate-600 group-hover:bg-slate-200 group-hover:scale-110"
                      }`}
                  >
                    {index + 1}
                  </div>

                  {/* Element Preview */}
                  <div
                    className={`flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden bg-white text-black border-2 transition-all duration-200 flex items-center justify-center
                      ${
                        isSelected
                          ? "border-primary-300 shadow-sm scale-105"
                          : "border-slate-200 group-hover:border-slate-300 group-hover:scale-105"
                      }`}
                  >
                    {getElementIcon(element)}
                  </div>

                  {/* Element Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div
                        className={`text-sm font-semibold truncate transition-colors
                          ${isSelected ? "text-primary-900" : "group-hover:text-slate-900"}`}
                      >
                        {getElementName(element)}
                      </div>
                      <span
                        className={`text-[10px] font-bold px-2 py-0.5 rounded-full transition-all duration-200
                          ${
                            isSelected
                              ? "bg-primary-500 text-white shadow-sm"
                              : "bg-slate-100 text-slate-600 group-hover:bg-slate-200"
                          }`}
                      >
                        z{element.zIndex}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 capitalize font-medium">
                      {element.type === "audioPlayer"
                        ? "Audio Player"
                        : getElementType(element.type)}
                    </div>
                  </div>
                </div>

                {/* Control Buttons - Compact Grid */}
                <div className="grid grid-cols-5 gap-1.5" onClick={(e) => e.stopPropagation()}>
                  {/* Bring to Front */}
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      dispatch(setSelectedId(element.id));
                      dispatch(bringToFront(element.id));
                    }}
                    disabled={isTopLayer}
                    title="Bring to Front"
                    variant="ghost"
                    size="sm"
                  >
                    <BiChevronsUp className="w-4 h-4" />
                  </IconButton>

                  {/* Bring Forward */}
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      dispatch(setSelectedId(element.id));
                      dispatch(bringForward(element.id));
                    }}
                    disabled={isTopLayer}
                    title="Bring Forward"
                    variant="ghost"
                    size="sm"
                  >
                    <BiCaretUp className="w-4 h-4" />
                  </IconButton>

                  {/* Send Backward */}
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      dispatch(setSelectedId(element.id));
                      dispatch(sendBackward(element.id));
                    }}
                    disabled={isBottomLayer}
                    title="Send Backward"
                    variant="ghost"
                    size="sm"
                  >
                    <BiCaretDown className="w-4 h-4" />
                  </IconButton>

                  {/* Send to Back */}
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      dispatch(setSelectedId(element.id));
                      dispatch(sendToBack(element.id));
                    }}
                    disabled={isBottomLayer}
                    title="Send to Back"
                    variant="ghost"
                    size="sm"
                  >
                    <BiChevronsDown className="w-4 h-4" />
                  </IconButton>

                  {/* Delete Button - Accent Color */}
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      dispatch(deleteElement(element.id));
                    }}
                    title="Delete Layer"
                    variant="ghost"
                    size="sm"
                    className="hover:bg-red-50 hover:text-red-600"
                  >
                    <BiTrash className="w-4 h-4" />
                  </IconButton>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty state */}
      {sortedElements.length === 0 && (
        <div className="py-12 px-4 text-center">
          <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-primary-100 to-accent-100 flex items-center justify-center transform hover:scale-110 transition-transform duration-200">
            <BiLayerPlus className="w-10 h-10 text-primary-400" />
          </div>
          <p className="text-sm font-semibold text-slate-700 mb-1">No layers yet</p>
          <p className="text-xs text-slate-500">Add elements to your slide to see them here</p>
        </div>
      )}

      {/* Pro tip - Only show when there are layers */}
      {sortedElements.length > 0 && (
        <div className="mt-4 p-3 bg-gradient-to-r from-primary-50 to-accent-50 border-2 border-primary-100 rounded-xl">
          <p className="text-xs text-primary-700 text-center font-medium">
            <span className="font-bold">Tip:</span> Higher z-index numbers appear on top
          </p>
        </div>
      )}
    </div>
  );
}
